import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { X, ZoomIn, ZoomOut, Crop, RotateCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(
          image,
          pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
          0, 0, pixelCrop.width, pixelCrop.height
        );
        canvas.toBlob((blob) => {
          if (blob && blob.size > 100) resolve(blob);
          else reject(new Error('Cropped image is empty or too small'));
        }, 'image/png', 0.95);
      } catch (err) {
        reject(err);
      }
    };
    image.onerror = () => reject(new Error('Failed to load image for cropping'));
    image.src = imageSrc;
  });
}

async function imageUrlToDataUrl(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Fetch failed');
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function ImageCropperModal({ imageSrc, onCropped, onClose, aspect = 1 }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [localImageUrl, setLocalImageUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const dataUrl = await imageUrlToDataUrl(imageSrc);
        setLocalImageUrl(dataUrl);
        setLoadError(null);
      } catch {
        try {
          const fullUrl = imageSrc.startsWith('http') ? imageSrc : `${window.location.origin}${imageSrc}`;
          const dataUrl = await imageUrlToDataUrl(fullUrl);
          setLocalImageUrl(dataUrl);
          setLoadError(null);
        } catch {
          setLoadError('Unable to load image for cropping');
        }
      }
    };
    loadImage();
  }, [imageSrc]);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels || !localImageUrl) return;
    setSaving(true);
    try {
      const blob = await getCroppedImg(localImageUrl, croppedAreaPixels);
      const uploadData = new FormData();
      uploadData.append('file', blob, 'cropped.png');

      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, uploadData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = response.data.url;
      onCropped(imageUrl);
    } catch (err) {
      console.error('Crop save error:', err);
      toast.error('Failed to save cropped image: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{loadError}</p>
          <button onClick={onClose} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold">
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!localImageUrl) {
    return (
      <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
        <p className="text-zinc-400">Loading image...</p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="image-cropper-modal">
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <h3 className="font-heading font-bold text-lg flex items-center gap-2">
          <Crop size={20} className="text-primary" /> Crop Image
        </h3>
        <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="cropper-close">
          <X size={24} />
        </button>
      </div>

      <div className="flex-1 relative">
        <Cropper
          image={localImageUrl}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onRotationChange={setRotation}
          onCropComplete={onCropComplete}
        />
      </div>

      <div className="p-4 border-t border-zinc-800 bg-zinc-950">
        <div className="flex items-center justify-between max-w-xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <ZoomOut size={16} className="text-zinc-400" />
              <input
                type="range" min={1} max={3} step={0.1} value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="w-28 accent-primary"
                data-testid="cropper-zoom"
              />
              <ZoomIn size={16} className="text-zinc-400" />
            </div>
            <button
              onClick={() => setRotation((r) => (r + 90) % 360)}
              className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 transition-colors"
              data-testid="cropper-rotate"
            >
              <RotateCw size={16} />
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-bold">
              Cancel
            </button>
            <button
              onClick={handleSave} disabled={saving}
              className="px-6 py-2 bg-primary hover:bg-primary/80 text-white text-sm font-bold disabled:opacity-50"
              data-testid="cropper-save"
            >
              {saving ? 'Saving...' : 'Save Crop'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
